import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, EmptyState, ListPage, Spinner } from '@nexvia-logistics/ui';
import { describeStorageError, getStorageQuotesMine, type StorageQuote } from '@/shared/storage-api';
import { formatCurrency, formatRelativeTime } from '@/shared/format';
import { StackIcon } from '@/shared/icons';
import { QuoteStatusChip } from '@/pages/QuoteStatusChip';
import { quoteStatusDetail, quoteTitle } from '@/pages/quote-status';

type LoadState = 'loading' | 'ready' | 'error';

/** NEX-1170 — `?requestId=` from the mails' link; anything but a positive integer is ignored. */
function linkedRequestIdFrom(value: string | null): number | null {
  const id = Number(value);
  return value && Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * `GET /storage/quotes/mine` (contract §"Cotizaciones del depósito"): every quote this depósito
 * already sent, with its status. The client is the one who accepts/rejects a quote; no action on
 * the quote itself lives here.
 *
 * Quote detail page (2026-10-28, Johan: "lo importante en el listado, toda la demás información
 * dándole click", like Mis solicitudes in the client web) — each row carries only what the mock's
 * row carries: "Solicitud #id · size", the client and when the quote was sent, the status detail
 * (how long it is valid, when it was accepted…), the monthly price and the status chip. The whole
 * row opens the quote's own page, `/cotizaciones/:id` (`CotizacionDetailPage`): prices for both
 * sides, what the quote offered, the request, and the guardado with its reception, withdrawal and
 * reviews.
 *
 * NEX-1170 / NEX-1329 — the quote-accepted and review mails link to `/cotizaciones?requestId=<id>`
 * (kept across `/login` by `returnTo`). The actions those mails ask for live on the quote's page
 * now, so the list goes straight there (replacing itself in the history), or says the quote is not
 * in the list.
 */
export default function CotizacionesPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [quotes, setQuotes] = useState<StorageQuote[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const linkedRequestId = linkedRequestIdFrom(searchParams.get('requestId'));
  const linkedQuote =
    linkedRequestId !== null ? (quotes.find((quote) => quote.storageRequestId === linkedRequestId) ?? null) : null;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      setError(null);
      try {
        const data = await getStorageQuotesMine();
        if (!cancelled) {
          setQuotes(data);
          setState('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setError(describeStorageError(err, 'No pudimos cargar tus cotizaciones.'));
          setState('error');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state === 'ready' && linkedQuote) navigate(`/cotizaciones/${linkedQuote.id}`, { replace: true });
  }, [state, linkedQuote, navigate]);

  return (
    <ListPage
      title="Mis cotizaciones"
      description="Lo que cotizaste y en qué quedó. Nexvia intermedia el cobro: el fee se monta sobre tu precio."
      notice={
        state === 'ready' && linkedRequestId !== null && !linkedQuote ? (
          <Alert tone="info">
            La cotización del mail no está en tu lista. Revisá que hayas entrado con la cuenta del depósito que
            cotizó esa solicitud.
          </Alert>
        ) : undefined
      }
    >
      {(state === 'loading' || linkedQuote) && (
        <p className="m-0 flex items-center gap-2 p-5 text-ui-body text-on-surface-variant">
          <Spinner size={18} label={null} />
          Cargando tus cotizaciones…
        </p>
      )}

      {state === 'error' && (
        <div className="p-5">
          <Alert tone="critical">{error}</Alert>
        </div>
      )}

      {state === 'ready' && !linkedQuote && quotes.length === 0 && (
        <EmptyState icon={<StackIcon size={24} />} title="Todavía no enviaste ninguna cotización." />
      )}

      {state === 'ready' && !linkedQuote && quotes.length > 0 && (
        <ul className="m-0 list-none p-0">
          {quotes.map((quote) => {
            const statusDetail = quoteStatusDetail(quote);
            return (
              <li
                key={quote.id}
                className="group relative p-4 shadow-[inset_0_-1px_0_var(--nx-outline-muted)] last:shadow-none hover:bg-state-row-hover md:p-5"
              >
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_13rem_13rem] xl:gap-5">
                  <span className="grid size-10 shrink-0 place-items-center rounded-card bg-primary-container text-primary">
                    <StackIcon size={20} />
                  </span>
                  <div className="min-w-0">
                    {/* The title's link stretches over the whole row (`after:inset-0` against the
                        `relative` row): one tab stop and a short name for screen readers, and the
                        whole row is the click target. The first and last rows touch `ListPage`'s
                        `overflow-hidden rounded-panel` (24px) edge, which clipped the ring's 16px
                        corners there: those two rows round the outer corners to the panel's radius. */}
                    <h2 className="m-0 text-ui-h3 text-on-surface">
                      <Link
                        to={`/cotizaciones/${quote.id}`}
                        className="text-current no-underline after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-card focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-primary group-first:focus-visible:after:rounded-t-panel group-last:focus-visible:after:rounded-b-panel"
                      >
                        {quoteTitle(quote)}
                      </Link>
                    </h2>
                    <p className="m-0 text-ui-small text-on-surface-variant">
                      {[quote.request?.clientName, `enviada ${formatRelativeTime(quote.createdAt)}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {/* Below xl the three last cells share one line under the title, wrapping when
                      they do not fit; from xl on they are the row's own columns, as in the mock.
                      The price and chip columns have a fixed width (Johan, 2026-10-28: the rows did
                      not line up): with `auto` each row sized them to its own text («Esperando
                      respuesta» is wider than «Aceptada»), which moved the status detail's column from
                      row to row. Every column starts its text at the same left edge in every row, the
                      title and the status detail share the free width evenly (the detail sits further
                      left, the price and the chip get more room), and 13rem fits «Esperando
                      respuesta» and a price up to about «$ 999.999,00 por mes». */}
                  <div className="col-span-2 col-start-1 flex flex-wrap items-center gap-x-4 gap-y-2 xl:contents">
                    <p className="m-0 text-ui-small text-on-surface-variant max-xl:basis-full max-xl:empty:hidden">
                      {statusDetail}
                    </p>
                    <p className="m-0 flex items-baseline gap-1.5 whitespace-nowrap">
                      <span className="text-ui-h2 text-on-surface">{formatCurrency(quote.monthlyPrice, quote.currency)}</span>
                      <span className="text-ui-small text-on-surface-variant">por mes</span>
                    </p>
                    <div className="max-xl:ml-auto xl:justify-self-start">
                      <QuoteStatusChip quote={quote} />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </ListPage>
  );
}
